import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

const ICON_STATUS_CLASSES = [
  'product-inventory__icon-low',
  'product-inventory__icon-in_stock',
  'product-inventory__icon-out_of_stock',
];

/**
 * @typedef {{ status: string, text: string }} VariantStockState
 *
 * @typedef {object} ProductInventoryRefs
 * @property {HTMLElement | undefined} inventoryIcon - The stock status icon.
 * @property {HTMLElement | undefined} inventoryText - The stock status text.
 * @property {HTMLScriptElement | undefined} variantStates - JSON stock states of every variant, keyed by variant ID.
 *
 * @extends {Component<ProductInventoryRefs>}
 */
class ProductInventory extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * Handles product select event by updating the inventory.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    this.#showVariantState(event.detail?.variantId);

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const newInventory = html.querySelector('product-inventory');
        if (!newInventory) return;

        morph(this, newInventory, { childrenOnly: true });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-inventory] Event promise rejected:', error);
      });
  };

  /**
   * Shows a variant's stock state from the data rendered with the block, so the count changes
   * as soon as a size is picked. The section response that follows replaces it with fresh data.
   * Variants missing from the data (e.g. another product in a combined listing) wait for it.
   * @param {string | undefined} variantId - The selected variant ID.
   */
  #showVariantState(variantId) {
    const { variantStates, inventoryIcon, inventoryText } = this.refs;
    if (!variantId || !variantStates || !inventoryText) return;

    /** @type {Record<string, VariantStockState>} */
    let states;
    try {
      states = JSON.parse(variantStates.textContent ?? '');
    } catch {
      return;
    }

    const state = states[variantId];
    if (!state) return;

    inventoryText.textContent = state.text;
    inventoryIcon?.classList.remove(...ICON_STATUS_CLASSES);
    inventoryIcon?.classList.add(`product-inventory__icon-${state.status}`);
  }
}

if (!customElements.get('product-inventory')) {
  customElements.define('product-inventory', ProductInventory);
}
